import { sendToApi } from 'api/client'
import { ExternalIntegration, ExternalIntegrationEnum } from './ExternalIntegrationsClient'
import { AlmanacFile, MediaObject, ScheduledPostTarget, User } from 'types'
import QueryString from 'qs'
import { SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { MetricSnapshot } from 'hooks/queries/externalIntegrations/types'

export interface CreateScheduledPostParams {
  postAt: string
  postData: any
  content?: string
  externalIntegrationId?: number
  mediaObjectIds?: number[]
  sourceId: string
  sourceType: string
  status?: 'scheduled' | 'draft'
  alternativeOptions?: string[]
  postSettings?: ScheduledPostSettings
}

export type ScheduledPostStatus = 'scheduled' | 'published' | 'failed' | 'retrying' | 'draft' | 'processing'

export enum ScheduledPostErrorType {
  ValidationError = 'validation_error',
  AuthorizationError = 'authorization_error',
  MediaFormatError = 'media_format_error',
  AuthenticationError = 'authentication_error',
  RateLimitError = 'rate_limit_error',
  NetworkError = 'network_error',
  UnknownError = 'unknown_error',
  UnexpectedError = 'unexpected_error',
}

export type ScheduledPost = {
  id: number
  post_at: string
  /**
   * @deprecated use content instead
   */
  post_data: any
  post_settings?: ScheduledPostSettings
  external_integration?: ExternalIntegration
  /**
   * @deprecated use the status from `usePostProperties` instead
   */
  status: ScheduledPostStatus
  source_id: string
  source_type: string
  post_url?: string
  source_title?: string
  error_message?: string
  failed_at?: string
  source_emoji?: string
  source_channel?: SocialChannelType
  content?: string
  postable: boolean
  error_type?: ScheduledPostErrorType
  alternative_options?: string[]
  is_posting?: boolean
  posting_failed?: boolean
  created_at: string
  updated_at: string
}

export type ScheduledPostShow = ScheduledPost & {
  user: User
  almanac_file: AlmanacFile
  media_objects: MediaObject[]
  media_files?: AlmanacFile[]
  scheduled_post_targets: ScheduledPostTarget[]
}

export type ScheduledPostWithLatestMetrics = ScheduledPostShow & {
  latest_metric_snapshots: MetricSnapshot[]
}

export type ScheduledPostsFilter = {
  source_id?: string
  source_type?: string
  project_id?: string
  status?: string
  order_by?: string
  page?: number
  items?: number
  external_integration_id?: string
  project_key?: string
  filter_by_post_at?: boolean
  channel?: string
}

export type ScheduledPostsCalendarFilter = {
  project_id: string
  start_date: string
  end_date: string
}

export type ScheduledPostSettings = TikTokPostSettings | EmailPostSettings | YoutubePostSettings

export type EmailPostSettings = {
  subject_line: string
  list_id?: string
  from_name?: string
  reply_to?: string
}

export type TikTokPostSettings = {
  privacy_level?: string
  disable_comment?: boolean
  disable_duet?: boolean
  disable_stitch?: boolean
  disclose_content?: boolean
  brand_organic_toggle?: boolean
  brand_content_toggle?: boolean
}

export type YoutubePostSettings = {
  privacy_level?: string
  title?: string
  description?: string
  category?: {
    id: string
    etag: string
    title: string
  }
  tags?: string[]
  age_level?: 'yes' | 'no'
}

export const ScheduledPostsClient = {
  create: async ({ workspaceId, params }: { workspaceId: number; params: CreateScheduledPostParams }) => {
    const res = await sendToApi(`/w/${workspaceId}/scheduled_posts?async=true`, {
      method: 'POST',
      body: {
        scheduled_post: {
          post_at: params.postAt,
          post_data: params.postData,
          external_integration_id: params.externalIntegrationId,
          media_object_ids: params.mediaObjectIds,
          source_id: params.sourceId,
          source_type: params.sourceType,
          status: params.status,
          content: params.content,
        },
      },
    })

    if (res.type === 'error') {
      throw new Error(res.statusText || 'API Error')
    }

    return res
  },
  publish: async ({ workspaceId, scheduledPostId }: { workspaceId: number; scheduledPostId: number }) => {
    const res = await sendToApi(`/w/${workspaceId}/scheduled_posts/${scheduledPostId}/publish`, {
      method: 'POST',
    })

    if (res.type === 'error') {
      throw new Error(res.data.error || 'Error publishing scheduled post')
    }

    return res
  },
  update: async ({
    workspaceId,
    scheduledPostId,
    params,
  }: {
    workspaceId: number
    scheduledPostId: number
    params: Partial<CreateScheduledPostParams>
  }) => {
    // Construct the scheduled_post object dynamically, only including keys that are not undefined
    const scheduledPost: any = {}
    if (params.postAt !== undefined) scheduledPost.post_at = params.postAt
    if (params.postData !== undefined) scheduledPost.post_data = params.postData
    if (params.externalIntegrationId !== undefined) scheduledPost.external_integration_id = params.externalIntegrationId
    if (params.mediaObjectIds !== undefined) scheduledPost.media_object_ids = params.mediaObjectIds
    if (params.status === 'scheduled') scheduledPost.status = params.status
    if (params.content !== undefined) scheduledPost.content = params.content
    if (params.alternativeOptions !== undefined) scheduledPost.alternative_options = params.alternativeOptions
    if (params.postSettings !== undefined) scheduledPost.post_settings = params.postSettings

    const res = await sendToApi<{ scheduled_post: ScheduledPostShow }>(
      `/w/${workspaceId}/scheduled_posts/${scheduledPostId}`,
      {
        method: 'PUT',
        body: { scheduled_post: scheduledPost },
      },
    )

    if (res.type === 'error') {
      throw new Error(res.statusText || 'API Error')
    }

    return res
  },
  generateCaption: async ({
    workspaceId,
    scheduledPostId,
    userMessage,
    provider,
  }: {
    workspaceId: number
    scheduledPostId: number
    userMessage?: string
    provider?: ExternalIntegrationEnum
  }) => {
    const body =
      userMessage || provider
        ? {
            ...(userMessage ? { user_message: userMessage } : {}),
            ...(provider ? { provider } : {}),
          }
        : undefined

    const res = await sendToApi<{ caption: string }>(
      `/w/${workspaceId}/scheduled_posts/${scheduledPostId}/generate_caption`,
      {
        method: 'POST',
        body,
      },
    )
    if (res.type === 'error') {
      throw new Error(res.statusText || 'API Error')
    }
    return res
  },
  delete: async ({ workspaceId, scheduledPostId }: { workspaceId: number; scheduledPostId: number }) => {
    const res = await sendToApi(`/w/${workspaceId}/scheduled_posts/${scheduledPostId}`, {
      method: 'DELETE',
    })

    if (res.type === 'error') {
      throw new Error(res.statusText || 'API Error')
    }

    return res
  },
  show: async ({ workspaceId, scheduledPostId }: { workspaceId: number; scheduledPostId: number }) => {
    return sendToApi<{ scheduled_post: ScheduledPostShow }>(`/w/${workspaceId}/scheduled_posts/${scheduledPostId}`)
  },
  index: async ({ workspaceId, filter }: { workspaceId: number; filter?: ScheduledPostsFilter }) => {
    const queryString = filter ? QueryString.stringify(filter) : ''

    return sendToApi<{ scheduled_posts: ScheduledPostShow[] }>(`/w/${workspaceId}/scheduled_posts?${queryString}`)
  },
  calendar: async ({ workspaceId, filter }: { workspaceId: number; filter?: ScheduledPostsCalendarFilter }) => {
    const queryString = filter ? QueryString.stringify(filter) : ''

    return sendToApi<{ scheduled_posts: ScheduledPost[] }>(`/w/${workspaceId}/scheduled_posts/calendar?${queryString}`)
  },
  fetchArticleScheduledPosts: async ({ workspaceId, articleId }: { workspaceId: number; articleId: string }) => {
    return sendToApi<{ scheduled_posts: ScheduledPost[] }>(
      `/w/${workspaceId}/scheduled_posts?source_id=${articleId}&source_type=Article&order_by=post_at.desc&include_drafts=true`,
    )
  },
  duplicate: async ({
    workspaceId,
    scheduledPostId,
    params,
  }: {
    workspaceId: number
    scheduledPostId: number
    params: { postAt?: string; postTo?: number[]; campaignId?: number }
  }) => {
    const res = await sendToApi<{ scheduled_post: ScheduledPostShow }>(
      `/w/${workspaceId}/scheduled_posts/${scheduledPostId}/duplicate`,
      {
        method: 'POST',
        body: {
          post_at: params.postAt,
          post_to: params.postTo,
          campaign_id: params.campaignId,
        },
      },
    )

    if (res.type === 'error') {
      throw new Error(res.statusText || 'API Error')
    }

    return res
  },
}
